import { useOnboarding } from '@/features/onboarding';
import { useOfferRequest } from '@/features/offer';
import {
  NotificationCategory,
  NotificationType,
  useNotifications,
} from '@/features/notifications';
import { useError } from '@/providers/Error';
import { genericMessages } from '@/constants/errorMessages';
import { useTerms } from './useTerms';

interface AcceptTermsOptions {
  // False when the BC consent was already given before the BC score.
  includesBc: boolean;
  acceptsMarketing: boolean;
  // Asked before KYC: the offer is requested after KYC, and ANAF is asked
  // again then if the lender cannot record it yet.
  isBeforeKyc?: boolean;
}

/**
 * What the terms screens save, for the chat: BC and ANAF consent, the offer
 * request that follows them, and the marketing opt-out when not given.
 */
export const useAcceptTerms = ({
  onError,
}: { onError?: (message: string) => void } = {}) => {
  const { setError } = useError();
  const reportError = onError ?? setError;
  const {
    acceptBcTerms,
    acceptPartnerAnafTerms,
    isLoading: isTermsLoading,
  } = useTerms();
  const { requestOffer, isLoading: isOfferLoading } = useOfferRequest();
  const { getOnboardingState } = useOnboarding();
  const { isUpdating: isUpdatingNotifications, updateNotificationSettings } =
    useNotifications();

  // Resolves true once the consents are saved and the onboarding state refreshed.
  const acceptTerms = async ({
    includesBc,
    acceptsMarketing,
    isBeforeKyc = false,
  }: AcceptTermsOptions) => {
    try {
      if (includesBc) {
        await acceptBcTerms();
      }

      if (isBeforeKyc) {
        await acceptPartnerAnafTerms().catch(() => undefined);
      } else {
        await acceptPartnerAnafTerms();
        await requestOffer();
      }

      if (!acceptsMarketing) {
        await updateNotificationSettings({
          category: NotificationCategory.Email,
          type: NotificationType.Marketing,
          isEnabled: false,
        });
      }

      await getOnboardingState();
      return true;
    } catch {
      reportError(genericMessages.unexpected);
      return false;
    }
  };

  return {
    acceptTerms,
    isLoading: isTermsLoading || isOfferLoading || isUpdatingNotifications,
  };
};
